import React from "react";

const TermsContent: React.FC = () => (
  <div className="bg-white p-4 p-md-5 rounded shadow-sm">
    <h1 className="h3 mb-2">Terms of Use for Send Updates AI</h1>
    <p className="text-muted mb-4">
      <strong>Last Updated:</strong> August 18, 2025
    </p>

    <h2 className="h5 mt-4">1. Acceptance of Terms</h2>
    <p>
      By accessing or using Send Updates AI (&quot;Service,&quot;
      &quot;Platform,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;),
      you (&quot;User,&quot; &quot;you,&quot; or &quot;your&quot;) agree to be
      bound by these Terms of Use (&quot;Terms&quot;) and all applicable laws
      and regulations. If you do not agree with any of these terms, you are
      prohibited from using the Service.
    </p>

    <h2 className="h5 mt-4">2. Service Description</h2>
    <p>Send Updates AI is a platform that:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Integrates with third-party services through OAuth authentication</li>
      <li>Aggregates and analyzes data from connected services</li>
      <li>
        Uses artificial intelligence to generate insights and recommendations
      </li>
      <li>
        Automates email communication based on aggregated data and insights
      </li>
      <li>Provides dashboard and reporting functionality</li>
    </ul>

    <h2 className="h5 mt-4">3. Account Registration and Security</h2>
    <h3 className="h6 mt-3">3.1 Account Creation</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        You must provide accurate and complete information during registration
      </li>
      <li>
        You are responsible for maintaining the confidentiality of your account
        credentials
      </li>
      <li>
        You must notify us immediately of any unauthorized use of your account
      </li>
      <li>
        You may not create accounts using automated means or false information
      </li>
    </ul>
    <h3 className="h6 mt-3">3.2 Account Eligibility</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        You must be at least 18 years old or the age of majority in your
        jurisdiction
      </li>
      <li>You must have the legal capacity to enter into this agreement</li>
      <li>
        You represent that you have authority to bind any organization on whose
        behalf you use the Service
      </li>
      <li>
        Corporate accounts require proper authorization from the organization
      </li>
    </ul>
    <h3 className="h6 mt-3">3.3 Security Responsibilities</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Maintain strong, unique passwords and enable two-factor authentication
      </li>
      <li>Promptly update account information when changes occur</li>
      <li>Monitor account activity and report suspicious behavior</li>
      <li>Comply with security recommendations and requirements</li>
    </ul>

    <h2 className="h5 mt-4">4. OAuth Integrations and Data Access</h2>
    <h3 className="h6 mt-3">4.1 Third-Party Authorization</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        You grant us permission to access third-party services on your behalf
        through OAuth
      </li>
      <li>
        You are responsible for maintaining valid authorization with integrated
        services
      </li>
      <li>
        You may revoke OAuth permissions at any time through your account
        settings
      </li>
      <li>
        We will cease accessing data from services where authorization has been
        revoked
      </li>
    </ul>
    <h3 className="h6 mt-3">4.2 Data Scope and Limitations</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>We access only data necessary to provide agreed-upon services</li>
      <li>You control which services to integrate and what data to share</li>
      <li>We respect the scope limitations defined in OAuth authorizations</li>
      <li>You may modify or restrict data access permissions at any time</li>
    </ul>
    <h3 className="h6 mt-3">4.3 Third-Party Terms Compliance</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        You must comply with the terms of service of all integrated third-party
        platforms
      </li>
      <li>
        You represent that your use of integrated services is authorized and
        compliant
      </li>
      <li>
        You are responsible for ensuring you have necessary rights to share data
        with us
      </li>
      <li>
        We are not responsible for changes to third-party service terms or
        availability
      </li>
    </ul>

    <h2 className="h5 mt-4">5. Acceptable Use Policy</h2>
    <h3 className="h6 mt-3">5.1 Permitted Uses</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Use the Service for legitimate business and personal purposes</li>
      <li>Integrate services you own or have authorization to access</li>
      <li>Create automated email communications for appropriate recipients</li>
      <li>Generate insights and reports based on your authorized data</li>
    </ul>
    <h3 className="h6 mt-3">5.2 Prohibited Uses</h3>
    <p>You may not:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Violate any applicable laws, regulations, or third-party rights</li>
      <li>
        Use the Service to transmit spam, unsolicited communications, or
        malicious content
      </li>
      <li>
        Attempt to circumvent security measures or access unauthorized data
      </li>
      <li>Reverse engineer, decompile, or attempt to extract source code</li>
      <li>Use the Service to compete with us or develop competing products</li>
      <li>Impersonate others or provide false information</li>
      <li>Interfere with or disrupt the Service or its infrastructure</li>
    </ul>
    <h3 className="h6 mt-3">5.3 Content Restrictions</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Email content must comply with CAN-SPAM Act and similar regulations
      </li>
      <li>Content must not be defamatory, harassing, or otherwise harmful</li>
      <li>
        You may not send content that infringes intellectual property rights
      </li>
      <li>
        Automated communications must include proper unsubscribe mechanisms
      </li>
    </ul>

    <h2 className="h5 mt-4">6. Intellectual Property Rights</h2>
    <h3 className="h6 mt-3">6.1 Our Intellectual Property</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        The Service, including software, designs, and documentation, is our
        property
      </li>
      <li>
        All trademarks, service marks, and logos are owned by us or our
        licensors
      </li>
      <li>
        You receive a limited, non-exclusive license to use the Service as
        intended
      </li>
      <li>This license terminates when your use of the Service ends</li>
    </ul>
    <h3 className="h6 mt-3">6.2 Your Content and Data</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        You retain ownership of data you provide or generate through integrated
        services
      </li>
      <li>
        You grant us a license to process your data solely to provide the
        Service
      </li>
      <li>AI-generated insights and recommendations are jointly owned</li>
      <li>
        You are responsible for ensuring you have rights to all data you share
      </li>
    </ul>
    <h3 className="h6 mt-3">6.3 Feedback and Suggestions</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Feedback you provide about the Service becomes our property</li>
      <li>We may use feedback to improve the Service without compensation</li>
      <li>You waive any rights to feedback you voluntarily provide</li>
      <li>We have no obligation to implement suggestions or feedback</li>
    </ul>

    <h2 className="h5 mt-4">7. Service Availability and Modifications</h2>
    <h3 className="h6 mt-3">7.1 Service Levels</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        We strive to maintain high availability but do not guarantee
        uninterrupted service
      </li>
      <li>Scheduled maintenance will be announced in advance when possible</li>
      <li>
        We are not liable for downtime caused by third-party service disruptions
      </li>
      <li>
        Service level agreements may be available for enterprise customers
      </li>
    </ul>
    <h3 className="h6 mt-3">7.2 Modifications and Updates</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>We may modify, update, or discontinue features at any time</li>
      <li>Material changes will be announced with reasonable advance notice</li>
      <li>
        Continued use after changes constitutes acceptance of modifications
      </li>
      <li>
        We may add new features or services that may be subject to additional
        terms
      </li>
    </ul>
    <h3 className="h6 mt-3">7.3 Beta Features</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Beta or experimental features are provided &quot;as is&quot; without
        warranties
      </li>
      <li>Beta features may be modified or discontinued without notice</li>
      <li>Participation in beta programs may be subject to additional terms</li>
      <li>
        Feedback on beta features is encouraged and may be used for improvements
      </li>
    </ul>

    <h2 className="h5 mt-4">8. Payment Terms and Billing</h2>
    <h3 className="h6 mt-3">8.1 Subscription Fees</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Fees are charged according to your selected subscription plan</li>
      <li>All fees are non-refundable unless otherwise specified</li>
      <li>Prices may change with 30 days&apos; advance notice</li>
      <li>Currency and tax obligations depend on your billing location</li>
    </ul>
    <h3 className="h6 mt-3">8.2 Payment Processing</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Payments are processed through third-party payment processors</li>
      <li>You authorize us to charge your selected payment method</li>
      <li>Failed payments may result in service suspension</li>
      <li>You are responsible for keeping payment information current</li>
    </ul>
    <h3 className="h6 mt-3">8.3 Usage-Based Charges</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Certain features may incur additional charges based on usage</li>
      <li>Usage charges will be clearly disclosed before you incur them</li>
      <li>Monthly usage limits may apply to prevent unexpected charges</li>
      <li>Overage charges will be billed in arrears</li>
    </ul>

    <h2 className="h5 mt-4">9. Privacy and Data Protection</h2>
    <h3 className="h6 mt-3">9.1 Privacy Policy</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Our Privacy Policy governs the collection and use of your information
      </li>
      <li>The Privacy Policy is incorporated into these Terms by reference</li>
      <li>We are committed to protecting your privacy and data security</li>
      <li>You consent to data processing as described in our Privacy Policy</li>
    </ul>
    <h3 className="h6 mt-3">9.2 Data Processing Agreement</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Enterprise customers may require separate Data Processing Agreements
      </li>
      <li>We act as a data processor for personal data you control</li>
      <li>
        We implement appropriate technical and organizational security measures
      </li>
      <li>Data processing complies with applicable privacy regulations</li>
    </ul>
    <h3 className="h6 mt-3">9.3 Data Export and Deletion</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>You may export your data in standard formats</li>
      <li>
        Account deletion will result in permanent data removal within specified
        timeframes
      </li>
      <li>Some data may be retained for legal or operational requirements</li>
      <li>
        Backup data will be securely deleted according to our retention policies
      </li>
    </ul>

    <h2 className="h5 mt-4">10. Limitation of Liability</h2>
    <h3 className="h6 mt-3">10.1 Disclaimer of Warranties</h3>
    <p>
      THE SERVICE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTIES OF ANY KIND.
      WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY,
      FITNESS FOR PURPOSE, AND NON-INFRINGEMENT.
    </p>
    <h3 className="h6 mt-3">10.2 Liability Limitations</h3>
    <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Our total liability is limited to the amount you paid in the 12 months
        preceding the claim
      </li>
      <li>
        We are not liable for indirect, incidental, or consequential damages
      </li>
      <li>
        We are not responsible for third-party service outages or data loss
      </li>
      <li>
        Time for bringing claims is limited to one year from when the claim
        arose
      </li>
    </ul>
    <h3 className="h6 mt-3">10.3 Essential Service Failures</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        If we fail to provide essential Service functions, our liability is
        limited to service credits
      </li>
      <li>
        Essential failures must be reported within 30 days to be eligible for
        remedies
      </li>
      <li>Service credits are your sole remedy for availability issues</li>
      <li>Credits do not extend subscription terms unless explicitly stated</li>
    </ul>

    <h2 className="h5 mt-4">11. Indemnification</h2>
    <p>You agree to indemnify and hold us harmless from claims arising from:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Your use of the Service in violation of these Terms</li>
      <li>Your violation of third-party rights or applicable laws</li>
      <li>Unauthorized access to integrated services through your account</li>
      <li>Content you send through our email automation features</li>
      <li>Breach of representations or warranties made in these Terms</li>
    </ul>

    <h2 className="h5 mt-4">12. Termination</h2>
    <h3 className="h6 mt-3">12.1 Termination by You</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        You may terminate your account at any time through account settings
      </li>
      <li>
        Cancellation takes effect at the end of your current billing period
      </li>
      <li>
        You remain responsible for all charges incurred before termination
      </li>
      <li>Data export should be completed before account closure</li>
    </ul>
    <h3 className="h6 mt-3">12.2 Termination by Us</h3>
    <p>We may suspend or terminate your account for:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Violation of these Terms or our policies</li>
      <li>Non-payment of fees or fraudulent payment activity</li>
      <li>Excessive or abusive use of the Service</li>
      <li>Legal or regulatory requirements</li>
    </ul>
    <h3 className="h6 mt-3">12.3 Effects of Termination</h3>
    <p>Upon termination:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Your access to the Service ceases immediately</li>
      <li>Data may be deleted after a reasonable grace period</li>
      <li>Outstanding fees become immediately due</li>
      <li>Provisions that by nature should survive will remain in effect</li>
    </ul>

    <h2 className="h5 mt-4">13. Dispute Resolution</h2>
    <h3 className="h6 mt-3">13.1 Informal Resolution</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Disputes should first be addressed through our support channels</li>
      <li>We will attempt to resolve issues within 30 days of notification</li>
      <li>Good faith negotiations are required before formal proceedings</li>
      <li>Documentation of informal resolution attempts may be required</li>
    </ul>
    <h3 className="h6 mt-3">13.2 Binding Arbitration</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Disputes not resolved informally will be subject to binding arbitration
      </li>
      <li>
        Arbitration will be conducted according to standard Arbitration Rules
      </li>
      <li>
        The arbitration location will be in the United States unless otherwise
        agreed
      </li>
      <li>
        Class action lawsuits are waived in favor of individual arbitration
      </li>
    </ul>
    <h3 className="h6 mt-3">13.3 Exceptions to Arbitration</h3>
    <p>The following disputes are exempt from arbitration requirements:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Intellectual property infringement claims</li>
      <li>Disputes seeking emergency injunctive relief</li>
      <li>Small claims court proceedings within jurisdictional limits</li>
      <li>Disputes related to the validity of arbitration clauses</li>
    </ul>

    <h2 className="h5 mt-4">14. Governing Law and Jurisdiction</h2>
    <h3 className="h6 mt-3">14.1 Applicable Law</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>These Terms are governed by the laws of the United States</li>
      <li>International use does not change the governing law</li>
      <li>Conflicts of law principles do not apply</li>
      <li>
        Local mandatory laws may also apply to users in certain jurisdictions
      </li>
    </ul>
    <h3 className="h6 mt-3">14.2 Jurisdiction</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Courts in the United States have exclusive jurisdiction for
        non-arbitrated disputes
      </li>
      <li>You consent to personal jurisdiction in these courts</li>
      <li>Venue is proper in the specified location</li>
      <li>Service of process may be made to your account email address</li>
    </ul>

    <h2 className="h5 mt-4">15. General Provisions</h2>
    <h3 className="h6 mt-3">15.1 Entire Agreement</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>These Terms constitute the complete agreement between the parties</li>
      <li>They supersede all previous agreements and understandings</li>
      <li>Modifications must be in writing and signed by both parties</li>
      <li>Enterprise customers may have additional contractual terms</li>
    </ul>
    <h3 className="h6 mt-3">15.2 Severability and Waiver</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Invalid provisions will be severed without affecting the remainder
      </li>
      <li>Waiver of any provision does not constitute a continuing waiver</li>
      <li>
        Our failure to enforce Terms does not waive our right to do so later
      </li>
      <li>Remedies are cumulative and not exclusive</li>
    </ul>
    <h3 className="h6 mt-3">15.3 Assignment</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>You may not assign these Terms without our written consent</li>
      <li>
        We may assign our rights and obligations to affiliates or successors
      </li>
      <li>
        Assignment includes business transfers and corporate reorganizations
      </li>
      <li>Permitted assignees must agree to honor these Terms</li>
    </ul>
    <h3 className="h6 mt-3">15.4 Force Majeure</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        We are not liable for delays caused by circumstances beyond our control
      </li>
      <li>
        Force majeure includes natural disasters, government actions, and
        infrastructure failures
      </li>
      <li>We will use reasonable efforts to minimize force majeure impacts</li>
      <li>Extended force majeure events may justify service modifications</li>
    </ul>

    <h2 className="h5 mt-4">16. Contact Information</h2>
    <p>For questions about these Terms:</p>
    <p>
      <strong>Send Updates AI Legal Team</strong>
      <br />
      Email: legal@sendupdates.ai
      {/* <br />
      Address: [Physical Address]
      <br />
      Phone: [Phone Number] */}
    </p>
    <p>
      <strong>Customer Support:</strong>
      <br />
      Email: support@sendupdatesai.com
      {/* <br />
      Help Center: [URL] */}
    </p>

    <h2 className="h5 mt-4">17. Effective Date and Changes</h2>
    <p>
      These Terms are effective as of August 18, 2025 and apply to all users. We
      may update these Terms periodically with notice to users. Continued use
      after changes constitutes acceptance of the updated Terms.
    </p>

    <hr />
    <p>
      <em>
        By using Send Updates AI, you acknowledge that you have read,
        understood, and agree to be bound by these Terms of Use.
      </em>
    </p>
  </div>
);

export default TermsContent;
